import React, { useState, useEffect } from 'react';
import { Calculator, Save, ChevronRight, ChevronLeft, RefreshCw, DollarSign, AlertCircle, Target, CheckCircle, Building2 } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import { leadRoiCalculatorService } from '../../services/leadRoiCalculatorService';
import { BarChart, Bar, PieChart as RechartsPie, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { getCurrencySymbol } from '../../utils/currencyFormatter';

export default function LeadROICalculator() {
  const { profile } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [hasCompletedWizard, setHasCompletedWizard] = useState(false);
  
  const [config, setConfig] = useState({
    id: null,
    configName: 'Lead Generation Configuration',
    currency: profile?.currency || 'ZAR',
    isActive: true,
    monthlyLeads: '',
    conversionRatePercentage: '',
    averageDealValue: '',
    totalMonthlyWonDeals: '',
    marketingManagementType: 'self_managed',
    ppcBudget: '',
    agencyRetainerFee: '',
    additionalBusinessCost: '',
    profitMarginPercentage: 30,
    overheadPercentage: 10
  });

  const [selectedServices, setSelectedServices] = useState({
    organicFacebookInstagram: false,
    paidMeta: false,
    paidGoogleAds: false,
    paidMicrosoftAds: false,
    newsletters: false,
    webDev: false
  });

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F472B6'];

  useEffect(() => {
    loadConfiguration();
  }, []);

  // Update currency when profile changes
  useEffect(() => {
    if (profile?.currency && config?.currency !== profile?.currency) {
      updateConfig({ currency: profile?.currency });
    }
  }, [profile?.currency]);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      setError('');
      const configs = await leadRoiCalculatorService?.getAllConfigs();
      
      const activeConfig = configs?.find(c => c?.isActive) || configs?.[0];
      if (activeConfig) {
        const sources = await leadRoiCalculatorService?.getLeadSources(activeConfig?.id);
        // Merge active config with current profile currency to ensure currency stays synced
        setConfig({
          ...activeConfig,
          currency: profile?.currency || activeConfig?.currency || 'ZAR',
          leadSources: sources || []
        });
        
        if (activeConfig?.monthlyLeads > 0) {
          setHasCompletedWizard(true);
          setCurrentStep(3);
        }
      }
    } catch (err) {
      setError(err?.message || 'Failed to load configuration');
      console.error('Error loading configuration:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      if (config?.id) {
        await leadRoiCalculatorService?.updateConfig(config?.id, config);
      } else {
        const newConfig = await leadRoiCalculatorService?.createConfig(config);
        setConfig(prev => ({ ...prev, id: newConfig?.id }));
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err) {
      setError(err?.message || 'Failed to save configuration');
      console.error('Error saving configuration:', err);
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (updates) => {
    setConfig(prev => ({ ...prev, ...updates }));
  };

  const handleLeadSourceUpdate = (sourceType, field, value) => {
    setConfig(prev => {
      const existingSource = prev?.leadSources?.find(s => s?.sourceType === sourceType);
      
      if (existingSource) {
        return {
          ...prev,
          leadSources: prev?.leadSources?.map(s =>
            s?.sourceType === sourceType ? { ...s, [field]: value } : s
          )
        };
      } else {
        return {
          ...prev,
          leadSources: [
            ...prev?.leadSources,
            {
              sourceType,
              [field]: value,
              monthlyCost: 0,
              monthlyLeads: 0,
              conversionRatePercentage: 0,
              isActive: true
            }
          ]
        };
      }
    });
  };

  const getLeadSourceValue = (sourceType, field) => {
    const source = config?.leadSources?.find(s => s?.sourceType === sourceType);
    return source?.[field] || 0;
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return `${getCurrencySymbol(config?.currency)} 0.00`;
    const formatted = parseFloat(value)?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${getCurrencySymbol(config?.currency)} ${formatted}`;
  };

  const formatNumberWithCommas = (value) => {
    if (!value && value !== 0) return '';
    const wholeNumber = Math.round(parseFloat(value));
    return wholeNumber?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const parseNumberWithCommas = (value) => {
    if (!value) return 0;
    return parseFloat(value?.replace(/,/g, '')) || 0;
  };

  const handleFormattedNumberChange = (value, field) => {
    const numericValue = parseNumberWithCommas(value);
    updateConfig({ [field]: numericValue });
  };

  const calculateMetrics = () => {
    const monthlyLeads = parseFloat(config?.monthlyLeads) || 0;
    const averageDealValue = parseFloat(config?.averageDealValue) || 0;
    const totalMonthlyWonDeals = parseFloat(config?.totalMonthlyWonDeals) || 0;
    const ppcBudget = parseFloat(config?.ppcBudget) || 0;
    const agencyRetainer = parseFloat(config?.agencyRetainerFee) || 0;
    const additionalCosts = parseFloat(config?.additionalBusinessCost) || 0;
    const profitMargin = parseFloat(config?.profitMarginPercentage) || 0;
    const overheadPercentage = parseFloat(config?.overheadPercentage) || 0;

    const totalMarketingBudget = config?.marketingManagementType === 'agency_managed' 
      ? ppcBudget + agencyRetainer + additionalCosts
      : ppcBudget + additionalCosts;

    // Auto-calculate conversion rate: (Won Deals / Total Leads) * 100
    // This represents the direct lead-to-customer conversion rate
    const conversionRate = monthlyLeads > 0 ? (totalMonthlyWonDeals / monthlyLeads) * 100 : 0;
    
    // Cost Per Lead = total marketing spend divided by ALL leads generated
    const costPerLead = monthlyLeads > 0 ? totalMarketingBudget / monthlyLeads : 0;
    
    // True CPA = cost to acquire an actual paying customer (won deal)
    const trueCPA = totalMonthlyWonDeals > 0 ? totalMarketingBudget / totalMonthlyWonDeals : 0;
    
    // Revenue is based on actual won deals
    const monthlyRevenue = totalMonthlyWonDeals * averageDealValue;
    const grossProfit = monthlyRevenue * (profitMargin / 100);
    const overheadCosts = monthlyRevenue * (overheadPercentage / 100);
    const netProfit = grossProfit - totalMarketingBudget - overheadCosts;
    const roi = totalMarketingBudget > 0 ? ((netProfit / totalMarketingBudget) * 100)?.toFixed(2) : 0;
    const roas = totalMarketingBudget > 0 ? ((monthlyRevenue / totalMarketingBudget))?.toFixed(2) : 0;

    return {
      monthlyLeads,
      totalMonthlyWonDeals,
      conversionRate,
      costPerLead,
      trueCPA,
      totalMarketingBudget,
      ppcBudget,
      agencyRetainer,
      additionalCosts,
      monthlyRevenue,
      grossProfit,
      overheadCosts,
      netProfit,
      roi,
      roas,
      netProfitMargin: monthlyRevenue > 0 ? (netProfit / monthlyRevenue) * 100 : 0
    };
  };

  const metrics = calculateMetrics();

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 3));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const restartWizard = () => {
    setCurrentStep(1);
    setHasCompletedWizard(false);
  };

  const completeWizard = async () => {
    await handleSave();
    setHasCompletedWizard(true);
    setCurrentStep(3);
  };

  const toggleService = (service) => {
    setSelectedServices(prev => ({
      ...prev,
      [service]: !prev?.[service]
    }));
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 overflow-auto p-8">
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading configuration...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        
        <main className="flex-1 overflow-auto">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Lead ROI Calculator & Analysis Engine
              </h1>
              <p className="text-gray-600">
                {!hasCompletedWizard && currentStep < 3 
                  ? 'Calculate your lead generation ROI and optimize marketing spend' :'Your Lead Generation ROI Overview'}
              </p>
            </div>

            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                <p className="font-medium">Error</p>
                <p className="text-sm">{error}</p>
              </div>
            )}

            {saveSuccess && (
              <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                <p className="text-sm">Configuration saved successfully!</p>
              </div>
            )}

            {!hasCompletedWizard && currentStep < 3 && (
              <div className="mb-8">
                <div className="flex justify-between mb-2">
                  {[1, 2]?.map((step) => (
                    <div
                      key={step}
                      className={`flex-1 h-2 mx-1 rounded-full ${
                        step <= currentStep ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-sm text-gray-600 text-center">
                  Step {currentStep} of 2
                </p>
              </div>
            )}

            {!hasCompletedWizard && currentStep < 3 && (
              <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
                {currentStep === 1 && (
                  <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Lead Metrics - Simple & Straightforward</h2>
                    
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <AlertCircle className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-blue-800">
                            <span className="font-semibold">Just answer these 2 simple questions:</span> We'll automatically calculate your conversion rate 
                            based on how many leads you receive versus how many deals you actually close. No guesswork needed!
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        How many total leads do you receive per month?
                      </label>
                      <input
                        type="text"
                        value={formatNumberWithCommas(config?.monthlyLeads)}
                        onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'monthlyLeads')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 100"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        💡 Total number of leads generated per month from all sources
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        How many deals do you actually WIN per month?
                      </label>
                      <input
                        type="text"
                        value={formatNumberWithCommas(config?.totalMonthlyWonDeals)}
                        onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'totalMonthlyWonDeals')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 10"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        🎯 Actual closed/won deals - customers who pay you
                      </p>
                    </div>

                    {config?.monthlyLeads > 0 && config?.totalMonthlyWonDeals > 0 && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-green-900 mb-1">
                              Your Conversion Rate (Auto-Calculated)
                            </p>
                            <p className="text-2xl font-bold text-green-700 mb-2">
                              {((config?.totalMonthlyWonDeals / config?.monthlyLeads) * 100)?.toFixed(1)}%
                            </p>
                            <p className="text-xs text-green-800">
                              Out of {formatNumberWithCommas(config?.monthlyLeads)} leads, you're winning {formatNumberWithCommas(config?.totalMonthlyWonDeals)} deals. 
                              That's a {((config?.totalMonthlyWonDeals / config?.monthlyLeads) * 100)?.toFixed(1)}% conversion rate from lead to customer!
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        What is your average deal value?
                      </label>
                      <input
                        type="text"
                        value={formatNumberWithCommas(config?.averageDealValue)}
                        onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'averageDealValue')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., 10,000"
                      />
                      <p className="text-xs text-gray-500 mt-1">Average revenue per won deal</p>
                    </div>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Marketing Management</h2>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Do you manage your own marketing or does a marketing agency manage it?
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <button
                          onClick={() => updateConfig({ marketingManagementType: 'self_managed' })}
                          className={`flex items-center justify-between p-4 border-2 rounded-lg transition-all ${
                            config?.marketingManagementType === 'self_managed' ?'border-blue-500 bg-blue-50' :'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <div className="flex items-center">
                            <CheckCircle className={`w-5 h-5 mr-3 ${
                              config?.marketingManagementType === 'self_managed' ? 'text-blue-600' : 'text-gray-400'
                            }`} />
                            <div className="text-left">
                              <p className="font-semibold text-gray-900">Self Managed</p>
                              <p className="text-xs text-gray-600">You handle all marketing activities</p>
                            </div>
                          </div>
                        </button>

                        <button
                          onClick={() => updateConfig({ marketingManagementType: 'agency_managed' })}
                          className={`flex items-center justify-between p-4 border-2 rounded-lg transition-all ${
                            config?.marketingManagementType === 'agency_managed' ?'border-blue-500 bg-blue-50' :'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          <div className="flex items-center">
                            <Building2 className={`w-5 h-5 mr-3 ${
                              config?.marketingManagementType === 'agency_managed' ? 'text-blue-600' : 'text-gray-400'
                            }`} />
                            <div className="text-left">
                              <p className="font-semibold text-gray-900">Agency Managed</p>
                              <p className="text-xs text-gray-600">Marketing agency handles activities</p>
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>

                    {config?.marketingManagementType === 'self_managed' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            What do you spend in PPC budget total?
                          </label>
                          <input
                            type="text"
                            value={formatNumberWithCommas(config?.ppcBudget)}
                            onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'ppcBudget')}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., 25,000"
                          />
                          <p className="text-xs text-gray-500 mt-1">Total monthly PPC advertising budget</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Additional Overheads
                          </label>
                          <input
                            type="text"
                            value={formatNumberWithCommas(config?.additionalBusinessCost)}
                            onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'additionalBusinessCost')}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., 5,000"
                          />
                          <p className="text-xs text-gray-500 mt-1">Any additional marketing-related costs</p>
                        </div>
                      </>
                    )}

                    {config?.marketingManagementType === 'agency_managed' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            What is your monthly retainer?
                          </label>
                          <input
                            type="text"
                            value={formatNumberWithCommas(config?.agencyRetainerFee)}
                            onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'agencyRetainerFee')}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., 15,000"
                          />
                          <p className="text-xs text-gray-500 mt-1">Monthly fee paid to your marketing agency</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Select marketing services included
                          </label>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {[
                              { key: 'organicFacebookInstagram', label: 'Organic Facebook/Instagram' },
                              { key: 'paidMeta', label: 'Paid Meta' },
                              { key: 'paidGoogleAds', label: 'Paid Google ADS' },
                              { key: 'paidMicrosoftAds', label: 'Paid Microsoft ADS' },
                              { key: 'newsletters', label: 'Newsletters' },
                              { key: 'webDev', label: 'Web Dev' }
                            ]?.map((service) => (
                              <label 
                                key={service?.key}
                                className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                                  selectedServices?.[service?.key]
                                    ? 'border-blue-500 bg-blue-50' :'border-gray-300 hover:border-gray-400'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedServices?.[service?.key]}
                                  onChange={() => toggleService(service?.key)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="ml-3 text-sm font-medium text-gray-700">{service?.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            What do you spend in PPC budget total?
                          </label>
                          <input
                            type="text"
                            value={formatNumberWithCommas(config?.ppcBudget)}
                            onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'ppcBudget')}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., 25,000"
                          />
                          <p className="text-xs text-gray-500 mt-1">Total monthly PPC advertising budget</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Additional overheads
                          </label>
                          <input
                            type="text"
                            value={formatNumberWithCommas(config?.additionalBusinessCost)}
                            onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'additionalBusinessCost')}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="e.g., 5,000"
                          />
                          <p className="text-xs text-gray-500 mt-1">Any additional marketing-related costs</p>
                        </div>
                      </>
                    )}
                  </div>
                )}

                <div className="flex justify-between mt-8 pt-6 border-t">
                  <button
                    onClick={prevStep}
                    disabled={currentStep === 1}
                    className="flex items-center px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5 mr-2" />
                    Previous
                  </button>

                  {currentStep < 2 ? (
                    <button
                      onClick={nextStep}
                      className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      Next
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </button>
                  ) : (
                    <button
                      onClick={completeWizard}
                      disabled={saving}
                      className="flex items-center px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      <Save className="w-5 h-5 mr-2" />
                      {saving ? 'Saving...' : 'Complete & View Overview'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {hasCompletedWizard && (
              <>
                <div className="mb-6 border-b border-gray-200">
                  <div className="flex space-x-8">
                    <button
                      onClick={() => setActiveTab('overview')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === 'overview' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      ROI Overview
                    </button>
                    <button
                      onClick={() => setActiveTab('breakdown')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === 'breakdown'
                          ? 'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Detailed Breakdown
                    </button>
                    <button
                      onClick={() => setActiveTab('settings')}
                      className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                        activeTab === 'settings' ?'border-blue-600 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Settings
                    </button>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-8">
                  {activeTab === 'overview' && (
                    <div className="space-y-6">
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">Lead Generation ROI Overview</h2>
                        <button
                          onClick={restartWizard}
                          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Rerun Wizard
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg shadow-sm border border-green-200 p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-xs font-semibold text-green-900 whitespace-nowrap">Total Leads In</h3>
                            <Target className="w-5 h-5 text-green-600 flex-shrink-0" />
                          </div>
                          <p className="text-lg font-bold text-green-900">
                            {formatNumberWithCommas(metrics?.monthlyLeads)}
                          </p>
                          <p className="text-xs text-green-700 mt-1">
                            📥 Leads received monthly
                          </p>
                        </div>

                        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg shadow-sm border border-indigo-200 p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-xs font-semibold text-indigo-900 whitespace-nowrap">Deals Won</h3>
                            <CheckCircle className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                          </div>
                          <p className="text-lg font-bold text-indigo-900">
                            {formatNumberWithCommas(metrics?.totalMonthlyWonDeals)}
                          </p>
                          <p className="text-xs text-indigo-700 mt-1">
                            🎉 {metrics?.conversionRate?.toFixed(1)}% conversion
                          </p>
                        </div>

                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg shadow-sm border border-purple-200 p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-xs font-semibold text-purple-900 whitespace-nowrap">Total Revenue</h3>
                            <DollarSign className="w-5 h-5 text-purple-600 flex-shrink-0" />
                          </div>
                          <p className="text-lg font-bold text-purple-900">
                            {formatCurrency(metrics?.monthlyRevenue)}
                          </p>
                          <p className="text-xs text-purple-700 mt-1">
                            💵 Monthly revenue generated
                          </p>
                        </div>

                        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg shadow-sm border border-red-200 p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-xs font-semibold text-red-900 whitespace-nowrap">Total Cost</h3>
                            <DollarSign className="w-5 h-5 text-red-600 flex-shrink-0" />
                          </div>
                          <p className="text-lg font-bold text-red-900">
                            {formatCurrency(metrics?.totalMarketingBudget)}
                          </p>
                          <p className="text-xs text-red-700 mt-1">
                            💳 Total marketing expenses
                          </p>
                        </div>

                        <div className={`bg-gradient-to-br rounded-lg shadow-sm border p-4 ${
                          metrics?.netProfit >= 0 
                            ? 'from-emerald-50 to-emerald-100 border-emerald-200' :'from-orange-50 to-orange-100 border-orange-200'
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <h3 className={`text-xs font-semibold ${
                              metrics?.netProfit >= 0 ? 'text-emerald-900' : 'text-orange-900'
                            }`}>
                              Net Profit
                            </h3>
                            <Calculator className={`w-5 h-5 flex-shrink-0 ${
                              metrics?.netProfit >= 0 ? 'text-emerald-600' : 'text-orange-600'
                            }`} />
                          </div>
                          <p className={`text-lg font-bold ${
                            metrics?.netProfit >= 0 ? 'text-emerald-900' : 'text-orange-900'
                          }`}>
                            {formatCurrency(metrics?.netProfit)}
                          </p>
                          <p className={`text-xs mt-1 ${
                            metrics?.netProfit >= 0 ? 'text-emerald-700' : 'text-orange-700'
                          }`}>
                            {metrics?.netProfit >= 0 
                              ? `✅ ${metrics?.roi}% ROI` 
                              : `⚠️ ${metrics?.roi}% ROI`}
                          </p>
                        </div>
                      </div>

                      {/* Additional KPI Cards Row */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                        <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-lg shadow-sm border border-cyan-200 p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-xs font-semibold text-cyan-900 whitespace-nowrap">CPL</h3>
                            <Target className="w-5 h-5 text-cyan-600 flex-shrink-0" />
                          </div>
                          <p className="text-lg font-bold text-cyan-900">
                            {formatCurrency(metrics?.costPerLead)}
                          </p>
                          <p className="text-xs text-cyan-700 mt-1">
                            💰 Cost Per Lead
                          </p>
                        </div>

                        <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg shadow-sm border border-amber-200 p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-xs font-semibold text-amber-900 whitespace-nowrap">CPA</h3>
                            <DollarSign className="w-5 h-5 text-amber-600 flex-shrink-0" />
                          </div>
                          <p className="text-lg font-bold text-amber-900">
                            {formatCurrency(metrics?.trueCPA)}
                          </p>
                          <p className="text-xs text-amber-700 mt-1">
                            🎯 Cost Per Acquisition
                          </p>
                        </div>

                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow-sm border border-blue-200 p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-xs font-semibold text-blue-900 whitespace-nowrap">ROAS</h3>
                            <Calculator className="w-5 h-5 text-blue-600 flex-shrink-0" />
                          </div>
                          <p className="text-lg font-bold text-blue-900">
                            {metrics?.roas}:1
                          </p>
                          <p className="text-xs text-blue-700 mt-1">
                            📊 Return on Ad Spend
                          </p>
                        </div>

                        <div className={`bg-gradient-to-br rounded-lg shadow-sm border p-4 ${
                          metrics?.roi >= 0 
                            ? 'from-teal-50 to-teal-100 border-teal-200' :'from-rose-50 to-rose-100 border-rose-200'
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <h3 className={`text-xs font-semibold ${
                              metrics?.roi >= 0 ? 'text-teal-900' : 'text-rose-900'
                            }`}>
                              ROI
                            </h3>
                            <Calculator className={`w-5 h-5 flex-shrink-0 ${
                              metrics?.roi >= 0 ? 'text-teal-600' : 'text-rose-600'
                            }`} />
                          </div>
                          <p className={`text-lg font-bold ${
                            metrics?.roi >= 0 ? 'text-teal-900' : 'text-rose-900'
                          }`}>
                            {metrics?.roi}%
                          </p>
                          <p className={`text-xs mt-1 ${
                            metrics?.roi >= 0 ? 'text-teal-700' : 'text-rose-700'
                          }`}>
                            {metrics?.roi >= 0 ? '✅ Return on Investment' : '⚠️ Return on Investment'}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                          <h3 className="text-lg font-bold text-slate-900 mb-4">Budget Allocation</h3>
                          <ResponsiveContainer width="100%" height={300}>
                            <RechartsPie>
                              <Pie
                                data={[
                                  { name: 'PPC Budget', value: metrics?.ppcBudget || 0 },
                                  ...(config?.marketingManagementType === 'agency_managed' 
                                    ? [{ name: 'Agency Retainer', value: metrics?.agencyRetainer || 0 }] 
                                    : []
                                  ),
                                  { name: 'Additional Costs', value: metrics?.additionalCosts || 0 }
                                ]?.filter(item => item?.value > 0)}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={90}
                                paddingAngle={3}
                                dataKey="value"
                                label={(entry) => `${formatCurrency(entry?.value)}`}
                              >
                                {[0, 1, 2]?.map((index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                                ))}
                              </Pie>
                              <Tooltip 
                                formatter={(value) => formatCurrency(value)}
                                contentStyle={{ 
                                  backgroundColor: 'white', 
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '8px'
                                }}
                              />
                              <Legend />
                            </RechartsPie>
                          </ResponsiveContainer>
                        </div>

                        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
                          <h3 className="text-lg font-bold text-slate-900 mb-4">Expense Breakdown</h3>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart
                              data={[
                                { name: 'PPC', value: metrics?.ppcBudget || 0 },
                                ...(config?.marketingManagementType === 'agency_managed' 
                                  ? [{ name: 'Retainer', value: metrics?.agencyRetainer || 0 }] 
                                  : []
                                ),
                                { name: 'Overheads', value: metrics?.additionalCosts || 0 }
                              ]?.filter(item => item?.value > 0)}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                              <YAxis tick={{ fontSize: 11 }} />
                              <Tooltip 
                                formatter={(value) => formatCurrency(value)}
                                contentStyle={{ 
                                  backgroundColor: 'white', 
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '8px'
                                }}
                              />
                              <Bar dataKey="value" fill="#3B82F6" radius={[8, 8, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-sm border border-blue-200 p-6">
                        <h3 className="text-lg font-bold text-blue-900 mb-4 flex items-center gap-2">
                          <AlertCircle className="w-5 h-5" />
                          Lead Generation Performance Insights
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div className="bg-white rounded-lg p-4 border border-blue-100">
                            <h4 className="font-semibold text-blue-900 mb-2">📊 Lead Conversion</h4>
                            <p className="text-sm text-slate-700 break-words">
                              {formatNumberWithCommas(metrics?.monthlyLeads)} leads → {formatNumberWithCommas(metrics?.totalMonthlyWonDeals)} won deals = {metrics?.conversionRate?.toFixed(1)}% conversion rate
                            </p>
                          </div>
                          <div className="bg-white rounded-lg p-4 border border-blue-100">
                            <h4 className="font-semibold text-blue-900 mb-2">🎯 Conversion Performance</h4>
                            <p className="text-sm text-slate-700 break-words">
                              {metrics?.totalMonthlyWonDeals > 0 
                                ? `You're converting ${metrics?.conversionRate?.toFixed(1)}% of leads into paying customers. That's ${formatNumberWithCommas(metrics?.totalMonthlyWonDeals)} deals closed per month!`
                                : '⚠️ Add your won deals to see your true lead-to-customer conversion rate and understand your sales effectiveness.'}
                            </p>
                          </div>
                          <div className="bg-white rounded-lg p-4 border border-blue-100">
                            <h4 className="font-semibold text-blue-900 mb-2">💰 True Cost Analysis</h4>
                            <p className="text-sm text-slate-700 break-words">
                              {metrics?.totalMonthlyWonDeals > 0 
                                ? `Your true cost per acquisition is ${formatCurrency(metrics?.trueCPA)}. This is what you actually spend to acquire a paying customer.`
                                : '⚠️ Track won deals to see your true customer acquisition cost. CPL doesn\'t tell the full story.'}
                            </p>
                          </div>
                          <div className="bg-white rounded-lg p-4 border border-blue-100">
                            <h4 className="font-semibold text-blue-900 mb-2">📈 ROAS Performance</h4>
                            <p className="text-sm text-slate-700 break-words">
                              {metrics?.roas >= 3 
                                ? `✅ Outstanding! ${metrics?.roas}x ROAS means you're generating ${metrics?.roas} in revenue for every R1 spent.` 
                                : metrics?.roas >= 2 
                                  ? `👍 Good! ${metrics?.roas}x ROAS. You're profitable - keep optimizing.` 
                                  : metrics?.roas > 1 
                                    ? `💡 ${metrics?.roas}x ROAS. Positive but room for improvement.` 
                                    : `⚠️ ROAS below 1x. Review your funnel urgently.`}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'breakdown' && (
                    <div className="space-y-6">
                      <h2 className="text-2xl font-bold text-gray-900 mb-6">Detailed Financial Breakdown</h2>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-700">Total Monthly Leads Received:</span>
                          <span className="font-semibold">{formatNumberWithCommas(metrics?.monthlyLeads)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-700">Total Deals Won:</span>
                          <span className="font-semibold">{formatNumberWithCommas(metrics?.totalMonthlyWonDeals)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-700">Lead-to-Customer Conversion Rate:</span>
                          <span className="font-semibold">{metrics?.conversionRate?.toFixed(1)}%</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-700">Average Deal Value:</span>
                          <span className="font-semibold">{formatCurrency(config?.averageDealValue)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-700">Cost Per Lead (CPL):</span>
                          <span className="font-semibold">{formatCurrency(metrics?.costPerLead)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100 bg-amber-50">
                          <span className="text-slate-900 font-medium">True Cost Per Acquisition (CPA):</span>
                          <span className="font-bold text-amber-700">{formatCurrency(metrics?.trueCPA)}</span>
                        </div>
                        
                        {/* Cost Breakdown Section */}
                        <div className="py-3 border-t-2 border-slate-300">
                          <h3 className="text-slate-900 font-bold mb-3">Cost Breakdown</h3>
                        </div>
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-700">PPC Budget:</span>
                          <span className="font-semibold">{formatCurrency(metrics?.ppcBudget)}</span>
                        </div>
                        {config?.marketingManagementType === 'agency_managed' && (
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-700">Agency Retainer Fee:</span>
                            <span className="font-semibold">{formatCurrency(metrics?.agencyRetainer)}</span>
                          </div>
                        )}
                        <div className="flex justify-between py-2 border-b border-slate-100">
                          <span className="text-slate-700">Additional Overheads:</span>
                          <span className="font-semibold">{formatCurrency(metrics?.additionalCosts)}</span>
                        </div>
                        <div className="flex justify-between py-3 border-t-2 border-slate-300">
                          <span className="text-slate-900 font-bold">Total Marketing Budget:</span>
                          <span className="font-bold text-lg">{formatCurrency(metrics?.totalMarketingBudget)}</span>
                        </div>
                        
                        {/* Revenue & Profit Section */}
                        <div className="flex justify-between py-2">
                          <span className="text-slate-900 font-bold">Monthly Revenue (from won deals):</span>
                          <span className="font-bold text-lg text-blue-600">{formatCurrency(metrics?.monthlyRevenue)}</span>
                        </div>
                        <div className="flex justify-between py-2">
                          <span className="text-slate-900 font-bold">Gross Profit ({config?.profitMarginPercentage}% margin):</span>
                          <span className="font-bold text-lg text-blue-600">{formatCurrency(metrics?.grossProfit)}</span>
                        </div>
                        <div className="flex justify-between py-2">
                          <span className="text-slate-900 font-bold">Overhead Costs ({config?.overheadPercentage}%):</span>
                          <span className="font-bold text-lg text-red-600">{formatCurrency(metrics?.overheadCosts)}</span>
                        </div>
                        <div className="flex justify-between py-2">
                          <span className="text-slate-900 font-bold">Net Profit (After All Costs):</span>
                          <span className={`font-bold text-lg ${metrics?.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(metrics?.netProfit)}
                          </span>
                        </div>
                        <div className="flex justify-between py-2">
                          <span className="text-slate-900 font-bold">Net Profit Margin:</span>
                          <span className={`font-bold text-lg ${metrics?.netProfitMargin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {metrics?.netProfitMargin?.toFixed(1)}%
                          </span>
                        </div>
                        <div className="flex justify-between py-3 border-t-2 border-slate-300">
                          <span className="text-slate-900 font-bold text-lg">Return on Investment (ROI):</span>
                          <span className={`font-bold text-2xl ${metrics?.roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {metrics?.roi}%
                          </span>
                        </div>

                        {/* Enhanced ROAS Section with Performance Indicators */}
                        <div className="mt-6 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border border-indigo-300 p-4">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-bold text-indigo-900">Current ROAS (Marketing Performance)</h3>
                            <span className={`font-bold text-3xl ${metrics?.roas >= 4 ? 'text-green-600' : metrics?.roas >= 2 ? 'text-blue-600' : 'text-orange-600'}`}>
                              {metrics?.roas}:1
                            </span>
                          </div>
                          
                          {/* Performance Indicator */}
                          <div className={`px-4 py-3 rounded-lg ${
                            metrics?.roas >= 4 ? 'bg-green-100 border border-green-300' : 
                            metrics?.roas >= 2 ? 'bg-blue-100 border border-blue-300': 'bg-orange-100 border border-orange-300'
                          }`}>
                            <p className={`text-sm font-semibold ${
                              metrics?.roas >= 4 ? 'text-green-800' : 
                              metrics?.roas >= 2 ? 'text-blue-800': 'text-orange-800'
                            }`}>
                              {metrics?.roas >= 4 
                                ? '✅ Excellent Marketing Performance! Your campaigns are generating strong returns.' 
                                : metrics?.roas >= 2 
                                  ? '👍 Good Marketing Performance! Your campaigns are performing well and generating positive returns.' 
                                  : '⚠️ Marketing Needs Optimization. Your campaigns aren\'t generating enough revenue to cover costs effectively.'}
                            </p>
                          </div>

                          {/* Explanation */}
                          <div className="mt-3 bg-white rounded-lg p-3 border border-indigo-200">
                            <p className="text-xs text-indigo-800 font-medium">
                              💡 <strong>What this means:</strong> For every {getCurrencySymbol(config?.currency)}1 spent on marketing, you're generating {getCurrencySymbol(config?.currency)}{metrics?.roas} in revenue. 
                              {metrics?.roas >= 4 
                                ? ' This is an excellent return that shows your marketing campaigns are highly effective.' 
                                : metrics?.roas >= 2 
                                  ? ' This is a solid return that shows your marketing campaigns are working well.' 
                                  : ' You may want to review your targeting, messaging, or lead qualification process to improve returns.'}
                            </p>
                          </div>

                          {/* Important Note */}
                          <div className="mt-3 bg-yellow-50 border border-yellow-300 rounded-lg p-3">
                            <p className="text-xs text-yellow-800 font-medium">
                              ⚠️ <strong>Important:</strong> ROAS measures marketing efficiency - revenue generated per dollar spent on ads. However, it doesn't account for overhead costs ({formatCurrency(metrics?.overheadCosts)}). 
                              {metrics?.roi < 0 && metrics?.roas >= 2 
                                ? ' Your marketing is performing well, but overall profitability is affected by other costs. Review your overhead expenses and profit margins.' 
                                : ' For complete business profitability, always refer to your Return on Investment (ROI) above, which factors in all costs.'}
                            </p>
                          </div>
                        </div>
                        
                        {/* 20% Comfortable Profit Target */}
                        <div className="mt-6 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg border border-amber-300 p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-bold text-amber-900">20% Comfortable Target for Healthy Profit</h3>
                            <span className={`text-lg font-bold ${metrics?.netProfitMargin >= 20 ? 'text-green-600' : 'text-orange-600'}`}>
                              {metrics?.netProfitMargin?.toFixed(1)}%
                            </span>
                          </div>
                          <div className="w-full bg-amber-200 rounded-full h-3 mb-2">
                            <div 
                              className={`h-3 rounded-full transition-all duration-500 ${
                                metrics?.netProfitMargin >= 20 ? 'bg-green-500' : 'bg-orange-500'
                              }`}
                              style={{ 
                                width: `${Math.min((metrics?.netProfitMargin / 20) * 100, 100)}%` 
                              }}
                            />
                          </div>
                          <p className="text-xs text-amber-800">
                            {metrics?.netProfitMargin >= 20
                              ? `✅ Excellent! Your net profit margin of ${metrics?.netProfitMargin?.toFixed(1)}% exceeds the comfortable 20% target. You have a healthy profit cushion.` 
                              : `⚠️ Your net profit margin is ${metrics?.netProfitMargin?.toFixed(1)}%. Aim for 20%+ net profit margin for a comfortable and sustainable business operation.`}
                          </p>
                        </div>
                      </div>

                      <div className="mt-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
                        <h4 className="font-semibold text-blue-900 mb-2">💡 Understanding Your Metrics</h4>
                        <ul className="text-sm text-blue-800 space-y-1">
                          <li>• <strong>Conversion Rate:</strong> Percentage of leads that become paying customers (Won Deals ÷ Total Leads)</li>
                          <li>• <strong>Cost Per Lead (CPL):</strong> What you spend to generate each lead ({formatCurrency(metrics?.totalMarketingBudget)} ÷ {formatNumberWithCommas(metrics?.monthlyLeads)} leads)</li>
                          <li>• <strong>True CPA:</strong> What you actually spend to acquire a paying customer ({formatCurrency(metrics?.totalMarketingBudget)} ÷ {formatNumberWithCommas(metrics?.totalMonthlyWonDeals)} won deals)</li>
                          <li>• <strong>ROAS:</strong> Revenue generated for every {getCurrencySymbol(config?.currency)}1 spent on marketing - measures campaign efficiency</li>
                          <li>• <strong>ROI:</strong> Overall return on investment after all costs - measures true business profitability</li>
                          <li>• <strong>Net Profit Margin:</strong> Percentage of revenue that becomes actual profit after all expenses</li>
                        </ul>
                      </div>
                    </div>
                  )}

                  {activeTab === 'settings' && (
                    <div className="space-y-6">
                      <h2 className="text-2xl font-bold text-gray-900 mb-6">Configuration Settings</h2>
                      
                      <div className="border-b pb-6">
                        <h4 className="font-medium text-gray-900 mb-4">Lead Metrics</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Total Monthly Leads</label>
                            <input
                              type="text"
                              value={formatNumberWithCommas(config?.monthlyLeads)}
                              onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'monthlyLeads')}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">Total leads received per month</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Total Monthly Won Deals</label>
                            <input
                              type="text"
                              value={formatNumberWithCommas(config?.totalMonthlyWonDeals)}
                              onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'totalMonthlyWonDeals')}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">Actual closed/won deals per month</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Average Deal Value</label>
                            <input
                              type="text"
                              value={formatNumberWithCommas(config?.averageDealValue)}
                              onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'averageDealValue')}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                            <label className="block text-xs font-medium text-blue-900 mb-1">Conversion Rate (Auto-Calculated)</label>
                            <p className="text-xl font-bold text-blue-700">
                              {metrics?.conversionRate?.toFixed(1)}%
                            </p>
                            <p className="text-xs text-blue-600 mt-1">
                              Based on {formatNumberWithCommas(config?.totalMonthlyWonDeals)} won deals ÷ {formatNumberWithCommas(config?.monthlyLeads)} total leads
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="border-b pb-6">
                        <h4 className="font-medium text-gray-900 mb-4">Marketing Management</h4>
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Management Type</label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <button
                              onClick={() => updateConfig({ marketingManagementType: 'self_managed' })}
                              className={`flex items-center justify-between p-3 border-2 rounded-lg transition-all text-sm ${
                                config?.marketingManagementType === 'self_managed' ?'border-blue-500 bg-blue-50' :'border-gray-300 hover:border-gray-400'
                              }`}
                            >
                              <span className="font-medium">Self Managed</span>
                              {config?.marketingManagementType === 'self_managed' && (
                                <CheckCircle className="w-4 h-4 text-blue-600" />
                              )}
                            </button>
                            <button
                              onClick={() => updateConfig({ marketingManagementType: 'agency_managed' })}
                              className={`flex items-center justify-between p-3 border-2 rounded-lg transition-all text-sm ${
                                config?.marketingManagementType === 'agency_managed' ?'border-blue-500 bg-blue-50' :'border-gray-300 hover:border-gray-400'
                              }`}
                            >
                              <span className="font-medium">Agency Managed</span>
                              {config?.marketingManagementType === 'agency_managed' && (
                                <CheckCircle className="w-4 h-4 text-blue-600" />
                              )}
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">PPC Budget</label>
                            <input
                              type="text"
                              value={formatNumberWithCommas(config?.ppcBudget)}
                              onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'ppcBudget')}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          {config?.marketingManagementType === 'agency_managed' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Agency Retainer Fee</label>
                              <input
                                type="text"
                                value={formatNumberWithCommas(config?.agencyRetainerFee)}
                                onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'agencyRetainerFee')}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          )}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Additional Business Cost</label>
                            <input
                              type="text"
                              value={formatNumberWithCommas(config?.additionalBusinessCost)}
                              onChange={(e) => handleFormattedNumberChange(e?.target?.value, 'additionalBusinessCost')}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <button
                          onClick={handleSave}
                          disabled={saving}
                          className="flex items-center px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}